import { useState, useEffect, useCallback, useMemo } from 'react';
import TrialBalanceTable from './TrialBalanceTable';
import AccountModal from './components/AccountModal';
import ImportTBModal from './components/ImportTBModal';
import { Button } from './components/Button';
import BulkActionPanel, { type BulkUpdate } from './components/BulkActionPanel';
import type { Engagement, Entity, TBAccount, JournalEntryHeader } from './types';
import { db, getAllJournalEntryLines, getEntities, getJournalEntryHeaders, updateAccountMAdjRef } from './db/database';
import { allowsSubGrouping, getFSRef } from './utils/mappings';
import type { NetIncomeValues } from './components/NetIncomeFooter';

interface WorkspaceProps {
  engagement: Engagement;
  isSidebarCollapsed: boolean;
  onOpenJournalEntry: (headerId: string) => void;
  onOpenMAdjustment: (headerId: string) => void;
}

// ============================================================================
// VIEW CONTROL STATE INTERFACE
// ============================================================================
export interface ViewState {
  showEntities: boolean;
  showFSRef: boolean;
  showGroupings: boolean;
  showSubGroupings: boolean;
  expandAll: boolean; // When true, show individual accounts
}

// Default: All four hierarchy levels visible for full data context on load
const DEFAULT_VIEW_STATE: ViewState = {
  showEntities: false,
  showFSRef: false,
  showGroupings: true,
  showSubGroupings: true,
  expandAll: false
};

// ============================================================================
// NET INCOME GROUPINGS
// ============================================================================
// Per Sprint 2.10 requirement:
// Net Income = (Sum of all accounts grouped as "Income" or "Deductions") * -1
const NET_INCOME_FS_REFS = new Set(['Income', 'Deductions']);

// Removed unused isSidebarCollapsed from props destructuring
export default function Workspace({ engagement, onOpenJournalEntry, onOpenMAdjustment }: WorkspaceProps) {
  // Fetch accounts from database
  const [accounts, setAccounts] = useState<TBAccount[]>([]);
  const [mAdjHeaders, setMAdjHeaders] = useState<JournalEntryHeader[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<TBAccount | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // ============================================================================
  // VIEW CONTROL STATE
  // ============================================================================
  const [viewState, setViewState] = useState<ViewState>(DEFAULT_VIEW_STATE);
  
  // ============================================================================
  // MULTI-SELECT STATE (LIFTED FROM TABLE)
  // ============================================================================
  // Using Set for O(1) lookup performance with large datasets
  const [selectedAccountIds, setSelectedAccountIds] = useState<Set<string>>(new Set());
  
  // ============================================================================
  // CELL SELECTION SUMMARY STATE (Sprint 2.10)
  // ============================================================================
  const [cellSelectionSummary, setCellSelectionSummary] = useState<{ count: number; sum: number }>({ count: 0, sum: 0 });
  
  // Modal state
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [isImportTBModalOpen, setIsImportTBModalOpen] = useState(false);
  const [accountToEdit, setAccountToEdit] = useState<TBAccount | null>(null);
  const [entities, setEntities] = useState<Entity[]>([]);

  const loadEntities = useCallback(async () => {
    if (engagement.id) {
      const list = await getEntities(engagement.id);
      setEntities(list);
    }
  }, [engagement.id]);

  useEffect(() => {
    loadEntities();
  }, [loadEntities]);

  const customEntities = entities.filter(e => e.name.toLowerCase() !== 'eliminations');

  useEffect(() => {
    if (customEntities.length <= 1) {
      setViewState(prev => ({ ...prev, showEntities: false }));
    }
  }, [customEntities.length]);

  const loadAccounts = useCallback(async () => {
    setIsLoading(true);
    try {
      if (engagement.id) {
        const [fetchedAccounts, allLines, allHeaders] = await Promise.all([
          db.accounts
            .where('engagementId')
            .equals(engagement.id)
            .sortBy('accountNumber'),
          getAllJournalEntryLines(engagement.id),
          getJournalEntryHeaders(engagement.id),
        ]);

        // Aggregate line amounts by accountId and type (integer cents)
        const rjeMap = new Map<string, number>();
        const ajeMap = new Map<string, number>();
        const mAdjMap = new Map<string, number>();

        // Build headerId → { id, jeNumber } lookup
        const headerById = new Map(allHeaders.map(h => [h.id!, { id: h.id!, jeNumber: h.jeNumber }]));

        // Build accountId → deduplicated list of { id, jeNumber }
        const accountJEsMap = new Map<string, Map<string, string>>();
        const accountMAdjMap = new Map<string, Map<string, string>>();

        for (const line of allLines) {
          if (line.type === 'RJE') {
            rjeMap.set(line.accountId, (rjeMap.get(line.accountId) ?? 0) + line.amount);
          } else if (line.type === 'AJE') {
            ajeMap.set(line.accountId, (ajeMap.get(line.accountId) ?? 0) + line.amount);
          } else if (line.type === 'M-ADJ') {
            mAdjMap.set(line.accountId, (mAdjMap.get(line.accountId) ?? 0) + line.amount);
          }
          const header = headerById.get(line.headerId);
          if (header) {
            if (line.type === 'M-ADJ') {
              if (!accountMAdjMap.has(line.accountId)) accountMAdjMap.set(line.accountId, new Map());
              accountMAdjMap.get(line.accountId)!.set(header.id, header.jeNumber);
            } else {
              if (!accountJEsMap.has(line.accountId)) accountJEsMap.set(line.accountId, new Map());
              accountJEsMap.get(line.accountId)!.set(header.id, header.jeNumber);
            }
          }
        }

        const accountsWithCalcs = fetchedAccounts.map(acc => {
          const rje = rjeMap.get(acc.id!) ?? 0;
          const aje = ajeMap.get(acc.id!) ?? 0;
          const mAdj = mAdjMap.get(acc.id!) ?? 0;
          const adjBook = acc.bookBalance + rje + aje;
          const jeMap = accountJEsMap.get(acc.id!);
          const relatedJEs = jeMap
            ? Array.from(jeMap.entries()).map(([id, jeNumber]) => ({ id, jeNumber }))
            : [];
          const mAdjJEMap = accountMAdjMap.get(acc.id!);
          const relatedMAdjs = mAdjJEMap
            ? Array.from(mAdjJEMap.entries()).map(([id, jeNumber]) => ({ id, jeNumber }))
            : [];
          const mAdjNumber = relatedMAdjs.length > 0
            ? relatedMAdjs.map(e => e.jeNumber).join(', ')
            : undefined;
          return {
            ...acc,
            rje,
            aje,
            mAdj,
            adjBook,
            ftax: adjBook + mAdj,
            relatedJEs,
            relatedMAdjs,
            mAdjNumber,
          };
        });

        setAccounts(accountsWithCalcs as TBAccount[]);
        setMAdjHeaders(allHeaders.filter((h) => h.netAdjustmentCents !== undefined));
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
      setAccounts([]);
    } finally {
      setIsLoading(false);
    }
  }, [engagement.id]);

  // Fetch accounts when engagement changes
  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // Format date as MM/DD/YYYY
  const formatDate = (isoDate: string): string => {
    const date = new Date(isoDate);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // ============================================================================
  // NET INCOME CALCULATION (Sprint 2.10)
  // ============================================================================
  // Computed from ALL accounts in the engagement, regardless of active filters.
  // Formula: (Sum of Income + Deductions accounts) * -1
  // Uses integer-cent math per System_Foundation.md.
  const netIncome: NetIncomeValues = useMemo(() => {
    // Filter to only Income and Deductions accounts
    const incomeDeductionAccounts = accounts.filter(acc =>
      NET_INCOME_FS_REFS.has(getFSRef(acc.grouping))
    );

    if (incomeDeductionAccounts.length === 0) {
      return { bookBalance: 0, rje: 0, aje: 0, adjBook: 0, mAdj: 0, ftax: 0 };
    }

    // Sum each column using integer addition (no floating point)
    let bookBalance = 0;
    let rje = 0;
    let aje = 0;
    let adjBook = 0;
    let mAdj = 0;
    let ftax = 0;

    for (const acc of incomeDeductionAccounts) {
      bookBalance += acc.bookBalance;
      rje += acc.rje;
      aje += acc.aje;
      adjBook += (acc.adjBook ?? 0);
      mAdj += acc.mAdj;
      ftax += (acc.ftax ?? 0);
    }

    // Multiply by -1 per requirement
    return {
      bookBalance: bookBalance * -1,
      rje: rje * -1,
      aje: aje * -1,
      adjBook: adjBook * -1,
      mAdj: mAdj * -1,
      ftax: ftax * -1,
    };
  }, [accounts]);

  // ============================================================================
  // SELECTION HANDLERS (LIFTED FROM TABLE)
  // ============================================================================

  /**
   * Handle selection changes from the table
   * This callback is passed down to TrialBalanceTable
   */
  const handleSelectionChange = useCallback((newSelection: Set<string>) => {
    setSelectedAccountIds(newSelection);
    console.log('Selection changed:', newSelection.size, 'accounts selected');
  }, []);

  /**
   * Clear all selections
   * Used when Bulk Action Panel is closed or after bulk operations
   */
  const handleClearSelection = useCallback(() => {
    setSelectedAccountIds(new Set());
  }, []);

  /**
   * Handle cell selection summary updates from the table (Sprint 2.10)
   */
  const handleCellSelectionChange = useCallback((summary: { count: number; sum: number }) => {
    setCellSelectionSummary(summary);
  }, []);

  const handleUpdateMAdjRef = useCallback(async (accountId: string, mAdjRef: string) => {
    await updateAccountMAdjRef(accountId, mAdjRef);
    await loadAccounts();
  }, [loadAccounts]);

  // ============================================================================
  // ROW INTERACTION HANDLERS
  // ============================================================================

  const handleRowClick = useCallback((account: TBAccount) => {
    setSelectedAccount(account);
    console.log('Row clicked:', account.accountNumber, account.accountName);
  }, []);

  const handleRowDoubleClick = useCallback((account: TBAccount) => {
    console.log('Row double-clicked - Opening edit modal:', account.accountNumber);
    setAccountToEdit(account);
    setIsAccountModalOpen(true);
  }, []);

  // ============================================================================
  // BULK ACTION HANDLERS
  // ============================================================================

  /**
   * Handle bulk update of selected accounts
   * Updates Grouping, SubGrouping, and/or M-Adj Ref for all selected accounts
   */
  const handleBulkUpdate = useCallback(async (updates: BulkUpdate) => {
    try {
      const selectedAccounts = accounts.filter(acc => selectedAccountIds.has(acc.id!));
      const now = Date.now();

      // Process each selected account
      for (const account of selectedAccounts) {
        const updatedAccount: TBAccount = { ...account };

        // Apply updates
        if (updates.grouping !== undefined) {
          updatedAccount.grouping = updates.grouping;
          
          // Auto-clear SubGrouping if new Grouping doesn't allow it
          if (!allowsSubGrouping(updates.grouping)) {
            updatedAccount.subGrouping = '';
          }
        }

        if (updates.subGrouping !== undefined) {
          // Only apply SubGrouping if current Grouping allows it
          if (allowsSubGrouping(updatedAccount.grouping)) {
            updatedAccount.subGrouping = updates.subGrouping;
          }
        }

        if (updates.mAdjRef !== undefined) {
          updatedAccount.mAdjRef = updates.mAdjRef;
        }

        updatedAccount.updatedAt = now;

        // Update in database
        await db.accounts.put(updatedAccount);
      }

      // Reload accounts to reflect changes
      await loadAccounts();

      // Clear selection after successful update
      handleClearSelection();

      console.log(`Successfully updated ${selectedAccounts.length} accounts`);
    } catch (error) {
      console.error('Bulk update error:', error);
      throw new Error('Failed to update accounts. Please try again.');
    }
  }, [accounts, selectedAccountIds, handleClearSelection, loadAccounts]);

  /**
   * Handle bulk delete of selected accounts
   * Enforces TB-002: Cannot delete accounts with PY balances or JE/M-Adj activity
   */
  const handleBulkDelete = useCallback(async () => {
    try {
      const selectedAccounts = accounts.filter(acc => selectedAccountIds.has(acc.id!));

      // TB-002 Protection: Check for PY balances
      const accountsWithPY = selectedAccounts.filter(acc => 
        acc.pyAdjBook !== 0 || acc.pyMAdj !== 0 || acc.pyFtax !== 0 || acc.pyBalance !== 0
      );

      if (accountsWithPY.length > 0) {
        const accountNumbers = accountsWithPY.map(acc => acc.accountNumber).join(', ');
        throw new Error(
          `Cannot delete accounts with Prior Year balances: ${accountNumbers}. ` +
          `This is prohibited by TB-002.`
        );
      }

      // Check for JE/M-Adj activity
      const accountsWithActivity = selectedAccounts.filter(acc => 
        acc.rje !== 0 || acc.aje !== 0 || acc.mAdj !== 0
      );

      if (accountsWithActivity.length > 0) {
        const accountNumbers = accountsWithActivity.map(acc => acc.accountNumber).join(', ');
        throw new Error(
          `Cannot delete accounts with Journal Entry or M-Adjustment activity: ${accountNumbers}`
        );
      }

      // All validations passed - proceed with delete
      const idsToDelete = selectedAccounts.map(acc => acc.id!);
      await db.accounts.bulkDelete(idsToDelete);

      // Reload accounts to reflect changes
      await loadAccounts();

      // Clear selection after successful delete
      handleClearSelection();

      console.log(`Successfully deleted ${selectedAccounts.length} accounts`);
    } catch (error) {
      console.error('Bulk delete error:', error);
      // Re-throw with original message if it's a validation error
      throw error;
    }
  }, [accounts, selectedAccountIds, handleClearSelection, loadAccounts]);

  // ============================================================================
  // MODAL HANDLERS
  // ============================================================================

  const handleAddAccount = () => {
    setAccountToEdit(null);
    setIsAccountModalOpen(true);
  };

  const handleImportTB = () => {
    setIsImportTBModalOpen(true);
  };

  const handleAccountModalClose = () => {
    setIsAccountModalOpen(false);
    setAccountToEdit(null);
  };

  const handleImportTBModalClose = () => {
    setIsImportTBModalOpen(false);
  };

  const handleModalSave = () => {
    // Refresh the accounts list after saving
    loadAccounts();
  };

  // ============================================================================
  // VIEW CONTROL HANDLERS
  // ============================================================================

  /**
   * FIX: Unified Expand/Collapse Toggle
   * Single button that toggles between expanded and collapsed states
   */
  const handleExpandCollapseToggle = useCallback(() => {
    setViewState(prev => ({
      ...prev,
      expandAll: !prev.expandAll
    }));
    console.log('View: Toggled expand/collapse');
  }, []);

  /**
   * Toggle Entities visibility
   */
  const handleEntities = useCallback(() => {
    setViewState(prev => ({
      ...prev,
      showEntities: !prev.showEntities
    }));
    console.log('View: Toggled entities');
  }, []);

  /**
   * Toggle FS Ref visibility
   */
  const handleFSRef = useCallback(() => {
    setViewState(prev => ({
      ...prev,
      showFSRef: !prev.showFSRef
    }));
    console.log('View: Toggled FS Ref');
  }, []);

  /**
   * Toggle Groupings visibility
   */
  const handleGroupings = useCallback(() => {
    setViewState(prev => ({
      ...prev,
      showGroupings: !prev.showGroupings
    }));
    console.log('View: Toggled groupings');
  }, []);

  /**
   * Toggle SubGroupings visibility
   */
  const handleSubGroupings = useCallback(() => {
    setViewState(prev => ({
      ...prev,
      showSubGroupings: !prev.showSubGroupings
    }));
    console.log('View: Toggled subgroupings');
  }, []);

  /**
   * Reset view to default (fully collapsed)
   */
  const handleReset = useCallback(() => {
    setViewState(DEFAULT_VIEW_STATE);
    console.log('View: Reset to default (fully collapsed)');
  }, []);

  // Get selected count for display
  const selectedCount = selectedAccountIds.size;

  // Track column visibility panel state (lifted from table) to drive margin shift
  const [isColumnPanelOpen, setIsColumnPanelOpen] = useState(false);

  return (
    <>
      <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <header className="flex-shrink-0 bg-white dark:bg-gray-800 border-b-2 border-gray-300 dark:border-gray-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {engagement.clientName} | {formatDate(engagement.taxYearEnd)}
            </h1>
            <div className="flex items-center gap-3">
              <Button variant="secondary" onClick={handleImportTB}>
                IMPORT TB
              </Button>
              <Button variant="primary" onClick={handleAddAccount}>
                ADD ACCOUNT
              </Button>
            </div>
          </div>
        </header>

        {/* Table Container - Adjust margin when either slide-out panel is open */}
        <div
          className="flex-1 overflow-hidden p-8 transition-all duration-300"
          style={{
            marginRight: (selectedCount > 0 || isColumnPanelOpen) ? '384px' : '0'
          }}
        >
          <div className="h-full bg-white dark:bg-gray-800 rounded-lg border-2 border-gray-300 dark:border-gray-600 shadow-sm overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-gray-300 dark:border-gray-600 border-t-green-600 dark:border-t-green-500 rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">Loading accounts...</p>
                </div>
              </div>
            ) : accounts.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center max-w-md px-4">
                  <p className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    No accounts found
                  </p>
                  <p className="text-gray-600 dark:text-gray-400">
                    Use "Add Account" or "Import TB" to begin building your trial balance.
                  </p>
                </div>
              </div>
            ) : (
              <TrialBalanceTable
                accounts={accounts}
                viewState={viewState}
                selectedAccountIds={selectedAccountIds}
                onSelectionChange={handleSelectionChange}
                onRowClick={handleRowClick}
                onRowDoubleClick={handleRowDoubleClick}
                onCellSelectionChange={handleCellSelectionChange}
                netIncome={netIncome}
                selectionCount={cellSelectionSummary.count}
                selectionSum={cellSelectionSummary.sum}
                onOpenJournalEntry={onOpenJournalEntry}
                onOpenMAdjustment={onOpenMAdjustment}
                mAdjHeaders={mAdjHeaders}
                onUpdateMAdjRef={handleUpdateMAdjRef}
                onExpandCollapse={handleExpandCollapseToggle}
                entityCount={customEntities.length}
                onToggleEntities={handleEntities}
                onToggleFSRef={handleFSRef}
                onToggleGroupings={handleGroupings}
                onToggleSubGroupings={handleSubGroupings}
                onReset={handleReset}
                onColumnPanelOpenChange={setIsColumnPanelOpen}
              />
            )}
          </div>
        </div>

        {/* Status bar with summary and selection info */}
        <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-t border-gray-300 dark:border-gray-600 px-6 py-2">
          <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
            <span>
              {accounts.length} account{accounts.length !== 1 ? 's' : ''}
              {selectedCount > 0 && (
                <span className="ml-4 text-blue-600 dark:text-blue-400 font-medium">
                  {selectedCount} selected
                </span>
              )}
            </span>
            {selectedAccount && !selectedAccountIds.has(selectedAccount.id!) && (
              <span>
                Selected: {selectedAccount.accountNumber} - {selectedAccount.accountName}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Account Modal */}
      <AccountModal
        isOpen={isAccountModalOpen}
        onClose={handleAccountModalClose}
        onSave={handleModalSave}
        engagement={engagement}
        entities={customEntities}
        accountToEdit={accountToEdit}
        mAdjHeaders={mAdjHeaders}
      />

      {/* Import TB Modal */}
      <ImportTBModal
        isOpen={isImportTBModalOpen}
        onClose={handleImportTBModalClose}
        onSave={handleModalSave}
        engagement={engagement}
        entities={customEntities}
      />

      {/* Bulk Action Panel - Only show when accounts are selected */}
      {selectedCount > 0 && (
        <BulkActionPanel
          selectedAccountIds={selectedAccountIds}
          accounts={accounts}
          engagementId={engagement.id!}
          mAdjHeaders={mAdjHeaders}
          onClose={handleClearSelection}
          onBulkUpdate={handleBulkUpdate}
          onBulkDelete={handleBulkDelete}
        />
      )}
    </>
  );
}

